/* =========================================================================
   supabase-client.ts — Supabase client for auth + scenario persistence.
   ========================================================================= */

import { createClient, SupabaseClient, User } from "@supabase/supabase-js";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? "";
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? "";

export function isSupabaseConfigured(): boolean {
  return Boolean(SUPABASE_URL && SUPABASE_KEY);
}

// A production bundle built without VITE_SUPABASE_* silently degrades to passcode-only auth
// with no backend sync (the 2026-06-12 env-less-deploy drift). This guard runs only in prod
// (import.meta.env.PROD) and only when the inlined creds are absent — a correctly-wired build
// and dev/test stay silent — so it fires only in a genuinely mis-built production bundle,
// turning that silent regression into a console signal. (Runs once at first import of this
// module, which is loaded eagerly via the app's persistence/auth layer.)
if (import.meta.env.PROD && !isSupabaseConfigured()) {
  console.error(
    "[auth] This build shipped WITHOUT VITE_SUPABASE_* — running passcode-fallback mode " +
      "(no Supabase sign-in, no cloud sync). The deploy was built without the Supabase env vars.",
  );
}

// `createClient` throws ("supabaseUrl is required") on an empty URL. When Supabase is not
// configured (no VITE_SUPABASE_* — e.g. passcode-only mode, a fresh clone, or CI without
// secrets), fall back to a harmless placeholder so this module loads instead of crashing the
// whole app at import. Every real Supabase call downstream is gated by isSupabaseConfigured(),
// so the placeholder client is never actually used.
export const supabase: SupabaseClient = createClient(
  SUPABASE_URL || "http://localhost:54321",
  SUPABASE_KEY || "public-anon-placeholder",
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  },
);

export type { User };
